import React from 'react';
import { Alert, Badge, Card, CardBody, CardHeader, Input, Table } from 'reactstrap';

import { Button } from '~components';

import dayjs, { dateDisplayFormat } from '~libs/dayjs';

import { DuplicateGroup } from '~support-at-home/actions/acoBudgets';

interface ComponentProps {
  duplicates: DuplicateGroup[];
  cleanCount: number;
  loserSelections: Record<number, Set<string>>;
  isResolving: boolean;
  onLoserToggle: (groupIndex: number, loserId: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  totalLoserCount: number;
}

const formatDate = (dateStr: string) => {
  const parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed.format(dateDisplayFormat) : dateStr;
};

const formatAmount = (amount: number | null) => {
  if (amount == null) return '—';
  return `$${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const DuplicateReviewTable: React.FC<ComponentProps> = ({
  duplicates,
  cleanCount,
  loserSelections,
  isResolving,
  onLoserToggle,
  onConfirm,
  onCancel,
  totalLoserCount,
}) => (
  <div data-cy="sah-budget-duplicate-review">
    <Alert color="warning" className="mb-3 p-2">
      <b>Duplicate budgets detected.</b>
      <div className="mt-1">
        {duplicates.length} client{duplicates.length !== 1 ? 's' : ''} with duplicate budgets found. Review each group
        below - select which existing records to remove when importing the new data.
      </div>
    </Alert>

    {cleanCount > 0 && (
      <div className="mb-3">
        <Badge color="success" pill>
          {cleanCount} clean record{cleanCount !== 1 ? 's' : ''}
        </Badge>
        <span className="text-muted ms-2 small">will be imported automatically</span>
      </div>
    )}

    {duplicates.map((group, groupIdx) => {
      const groupLoserCount = loserSelections[groupIdx]?.size ?? 0;

      return (
        <Card key={groupIdx} className="mb-3 rounded-0" data-cy={`sah-budget-duplicate-group-${groupIdx}`}>
          <CardHeader className="py-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-bold">{group.client_name}</span>
              <Badge color="info" pill className="small">
                {group.funding_type}
              </Badge>
              <span className="text-muted small">
                {formatDate(group.start_date)} - {formatDate(group.end_date)}
              </span>
              {groupLoserCount > 0 && (
                <Badge color="danger" pill className="ms-auto small">
                  {groupLoserCount} to remove
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardBody className="p-0">
            <Table size="sm" className="mb-0" responsive>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <span className="visually-hidden">Select for removal</span>
                  </th>
                  <th>Role</th>
                  <th>Budget ID</th>
                  <th className="text-end">Total</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {/* Winner row — incoming record */}
                <tr className="table-success" data-cy={`sah-budget-winner-row-${groupIdx}`}>
                  <td />
                  <td>
                    <Badge color="success">Winner</Badge>
                  </td>
                  <td>
                    <code>{group.incoming.sah_budget_id}</code>
                  </td>
                  <td className="text-end">{formatAmount(group.incoming.total_amount)}</td>
                  <td className="text-muted small">Will be upserted</td>
                </tr>

                {/* Loser rows — existing records */}
                {group.existing.map((ex) => {
                  const isSelected = loserSelections[groupIdx]?.has(ex.id) ?? false;
                  return (
                    <tr
                      key={ex.id}
                      className={isSelected ? 'table-danger' : ''}
                      data-cy={`sah-budget-loser-row-${groupIdx}-${ex.id}`}
                    >
                      <td className="text-center">
                        <Input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onLoserToggle(groupIdx, ex.id)}
                          data-cy={`sah-budget-loser-checkbox-${groupIdx}-${ex.id}`}
                          aria-label={`Select budget ${ex.sah_budget_id} for removal`}
                        />
                      </td>
                      <td>
                        <Badge color="danger">Loser</Badge>
                      </td>
                      <td>
                        {isSelected ? (
                          <s className="text-muted">
                            <code>{ex.sah_budget_id}</code>
                          </s>
                        ) : (
                          <code>{ex.sah_budget_id}</code>
                        )}
                      </td>
                      <td className="text-end">{formatAmount(ex.total_amount)}</td>
                      <td className="small">
                        {isSelected ? (
                          <span className="text-danger">Will be deleted</span>
                        ) : (
                          <span className="text-muted">Will be kept</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      );
    })}

    <div className="d-flex justify-content-between align-items-center mt-3">
      <Button
        id="sah-budget-cancel-merge"
        size="sm"
        color="secondary"
        onClick={onCancel}
        data-cy="sah-budget-cancel-merge-button"
      >
        Cancel
      </Button>
      <div className="d-flex align-items-center gap-2">
        {totalLoserCount > 0 && (
          <span className="text-muted small">
            {totalLoserCount} record{totalLoserCount !== 1 ? 's' : ''} will be removed
          </span>
        )}
        <Button
          id="sah-budget-confirm-merge"
          size="sm"
          color="success"
          onClick={onConfirm}
          disabled={isResolving}
          data-cy="sah-budget-confirm-merge-button"
        >
          {isResolving ? 'Processing…' : 'Confirm & Import'}
        </Button>
      </div>
    </div>
  </div>
);

export default DuplicateReviewTable;
